import { addDays, format, isAfter } from 'date-fns';
import { getStartAndEndOfWeek, simpleDateToDate } from './dates';
import type { SimpleDate } from './types';

/*
  Where a player goes after finishing a puzzle.

  Only the first rule of the website's ladder is here — the rest of this game's week — because it's
  the only one both clients can answer: the others need every game's statuses, the intro-mode map and
  a results page the app has none of. What is shared is the rule itself and the way the button reads,
  so the two clients don't propose the same day in different words.
*/

/**
 * The next day of the same week that's worth offering: after the one just played, not later than
 * today, and not already finished.
 *
 * `isOpen` answers "is there something to play on this date" — a board exists and it isn't complete.
 * Callers hold that knowledge in different shapes (the website has an entry per board, the app a map
 * of the days it has touched), so it's a question rather than a list.
 */
export function nextDayThisWeek(
  played: SimpleDate,
  today: SimpleDate,
  isOpen: (date: SimpleDate) => boolean
): SimpleDate | undefined {
  const [, weekEnd] = getStartAndEndOfWeek(played);
  const last = today < weekEnd ? today : weekEnd;

  let cursor = addDays(simpleDateToDate(played), 1);
  while (!isAfter(cursor, simpleDateToDate(last))) {
    const date = format(cursor, 'yyyy-MM-dd') as SimpleDate;
    if (isOpen(date)) return date;
    cursor = addDays(cursor, 1);
  }

  return undefined;
}

/** How the button names a day: "Play Thursday's Puzzle". */
export const nextDayLabel = (date: SimpleDate): string => `Play ${format(simpleDateToDate(date), 'EEEE')}'s Puzzle`;

/** And when there's no such day, the archive gets the offer instead. */
export const moreGameLabel = (gameLabel: string): string => `Play More ${gameLabel}`;
